import {
  CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis,
} from 'recharts';
import CustomDropdown from '../../components/CustomDropdown/CustomDropdown';
import usePenetrationReportV2Store from '../../stores/penetrationReportV2Store';
import { TREND_RANGE_OPTIONS, TrendPoint } from '../../services/penetrationReportV2/types';
import { formatPeriod, shiftPeriod } from '../../services/penetrationReportV2/periodUtils';

const RANGE_OPTIONS = TREND_RANGE_OPTIONS.map((months) => ({ label: `${months} months`, value: String(months) }));

interface TrendChartProps {
  trend: TrendPoint[];
  comparisonEnabled: boolean;
}

function TrendChart({ trend, comparisonEnabled }: TrendChartProps) {
  const asOfPeriod = usePenetrationReportV2Store((s) => s.asOfPeriod);
  const comparePeriod = usePenetrationReportV2Store((s) => s.comparePeriod);
  const trendRangeMonths = usePenetrationReportV2Store((s) => s.trendRangeMonths);
  const setTrendRangeMonths = usePenetrationReportV2Store((s) => s.setTrendRangeMonths);

  const first = trend[0];
  const last = trend[trend.length - 1];
  const deltaPp = first && last ? Math.round((last.currentPct - first.currentPct) * 10) / 10 : 0;

  const currentRangeStart = shiftPeriod(asOfPeriod, -(trendRangeMonths - 1));
  const compareRangeStart = shiftPeriod(comparePeriod, -(trendRangeMonths - 1));

  return (
    <div className="pr2-trend">
      <div className="pr2-heading">
        <div>
          <h2>Penetration trend</h2>
          <p>Current period compared with the selected comparison period</p>
        </div>
        <CustomDropdown
          className="pr2-range-select"
          value={String(trendRangeMonths)}
          options={RANGE_OPTIONS}
          onChange={(value) => setTrendRangeMonths(Number(value))}
        />
      </div>
      <div className="pr2-legend">
        <span><i /> {formatPeriod(currentRangeStart)} – {formatPeriod(asOfPeriod)}</span>
        {comparisonEnabled && (
          <span><i className="dashed" /> {formatPeriod(compareRangeStart)} – {formatPeriod(comparePeriod)}</span>
        )}
      </div>
      <div className="pr2-trend-delta">
        <strong>{deltaPp >= 0 ? '+' : ''}{deltaPp.toFixed(1)}</strong>
        <small>percentage points</small>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={trend} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#edf0f4" />
          <XAxis dataKey="monthLabel" tick={{ fontSize: 10, fill: '#98a2b3' }} axisLine={false} tickLine={false} />
          <YAxis
            tick={{ fontSize: 10, fill: '#98a2b3' }}
            axisLine={false}
            tickLine={false}
            width={40}
            tickFormatter={(v: number) => `${v.toFixed(1)}%`}
            domain={['auto', 'auto']}
          />
          <ReTooltip
            content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const point = payload[0]?.payload as TrendPoint;
              return (
                <div className="pr2-tooltip">
                  <div className="pr2-tooltip-row"><span>{point.monthLabel}</span><strong>{point.currentPct.toFixed(1)}%</strong></div>
                  {comparisonEnabled && (
                    <div className="pr2-tooltip-row"><span>{point.priorMonthLabel} (prior)</span><strong>{point.priorPct.toFixed(1)}%</strong></div>
                  )}
                </div>
              );
            }}
          />
          <Line type="monotone" dataKey="currentPct" name="Current" stroke="#1554c4" strokeWidth={2.2} dot={false} />
          {comparisonEnabled && (
            <Line type="monotone" dataKey="priorPct" name="Prior" stroke="#98a2b3" strokeWidth={1.7} strokeDasharray="4 3" dot={false} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TrendChart;
